import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Checkbox,
  Modal,
  PasswordInput,
  PinInput,
  Text,
  TextInput,
  Paper,
  Title,
  Group,
  Anchor,
  Box,
  ThemeIcon,
  Stack,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconMail,
  IconLock,
  IconBrandGoogle,
} from "@tabler/icons-react";
import { signInWithPopup } from "@firebase/auth";
import { auth, googleProvider } from "../../Lib/Firebase";
import { motion } from "framer-motion";

import {
  AppName,
  Colors,
  LogoFullWhite,
  LogoM,
  OtpWaitTime,
  ValidOtpLength,
} from "../../Lib/Data";
import { Endpoints } from "../../Lib/Endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  trimString,
  validateEmail,
} from "../../Lib/Methods";
import { NonPaginatedResponse } from "../../Lib/Responses";
import { LoginResponse } from "../../Lib/Types";
import { PerformRequest } from "../../Lib/usePerformRequest";
import "./styles.scss";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  document.title = `Login | ${AppName}`;

  interface LoginFormProps {
    email: string;
    password: string;
  }
  const loginForm = useForm<LoginFormProps>({
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value) =>
        validateEmail(value) ? null : "Please enter a valid email!",
      password: (value) =>
        value && value.length >= 6 ? null : "At LEAST 6 characters!",
    },
  });

  const searchParams = new URLSearchParams(location.search);
  const isRedirectToCart = searchParams.get("cart") === "yes";
  if (!isRedirectToCart) {
    localStorage.removeItem("pending_period");
    localStorage.removeItem("pending_services");
  }
  const [isKeepUserLoggedIn, setKeepUserLoggedIn] = useState<boolean>(false);
  const [isLoggingIn, setLoggingIn] = useState<boolean>(false);
  const [isGoogleLoggingIn, setGoogleLoggingIn] = useState<boolean>(false);

  // token and setToken are only required for email otp authorization
  const [token, setToken] = useState<string>("");

  const [isOtpModalOpen, { open: OpenOtpModal, close: CloseOtpModal }] =
    useDisclosure(false);

  const [resendOtpCounter, setResendOtpCounter] = useState<number>(OtpWaitTime);
  const [isResendingOtp, setIsResendingOtp] = useState<boolean>(false);
  const otpForm = useForm({
    initialValues: {
      otp: "",
    },
    validate: {
      otp: (value) =>
        value && value.length === ValidOtpLength ? null : "Enter a valid OTP",
    },
  });

  // IF THE USER SPECIFIES KEEP LOGGED IN, GENERATE LONGER LASTING TOKEN
  const LoginUser = async () => {
    const { hasErrors: formHasErrors } = loginForm.validate();
    if (!formHasErrors) {
      const { email, password } = loginForm.values;
      setLoggingIn(true);
      const r = await PerformRequest<NonPaginatedResponse<LoginResponse>>({
        method: "POST",
        route: Endpoints.GetLogin(),
        data: {
          email: trimString(email.toLowerCase()),
          password: trimString(password),
          stayLoggedIn: isKeepUserLoggedIn,
        },
        callback: () => {
          setLoggingIn(false);
        },
      });
      setLoggingIn(false);

      if (r) {
        if (r.status === 202) {
          // EMAIL OTP AUTH REQUIRED
          setToken(r?.data?.data?.token);
          OpenOtpModal();
          let count = OtpWaitTime;
          const intervalId = setInterval(() => {
            count -= 1;
            setResendOtpCounter(count);

            // Stop the interval after 5 runs
            if (count === 0) {
              clearInterval(intervalId);
              setResendOtpCounter(0);
            }
          }, 1000); // Runs every 1 second
        } else {
          if (r.status === 200) {
            localStorage.setItem("t", r.data.data.token);
            navigate("/d/services");
          } else if (r.status === 404) {
            DefaultErrorNotification("Account does not exist!");
          } else if (r.status === 401) {
            DefaultErrorNotification("Invalid email or password");
          }
        }
        if (!r.status) {
          DefaultErrorNotification("An error occurred!");
        }
      } else {
        DefaultErrorNotification("An error occurred!");
      }
    }
  };

  const ResendOtp = async () => {
    const { hasErrors: formHasErrors } = loginForm.validate();
    if (!formHasErrors) {
      const { email } = loginForm.values;
      setIsResendingOtp(true);
      const r = await PerformRequest<NonPaginatedResponse<LoginResponse>>({
        method: "GET",
        route: Endpoints.GetSendOtpToUser(email),
        callback: () => {
          setIsResendingOtp(false);
        },
      });
      setIsResendingOtp(false);
      if (r) {
        if (r.status === 200) {
          DefaultSuccessNotification("OTP sent to your email!");
          let count = OtpWaitTime;
          const intervalId = setInterval(() => {
            count -= 1;
            setResendOtpCounter(count);

            // Stop the interval after 5 runs
            if (count === 0) {
              clearInterval(intervalId);
              setResendOtpCounter(0);
            }
          }, 1000); // Runs every 1 second
        }
      } else {
        DefaultErrorNotification("An error occurred!");
      }
    }
  };

  const ValidateOtpAndLogin = async () => {
    const { hasErrors: formHasErrors } = otpForm.validate();
    const { hasErrors: loginFormHasErrors } = loginForm.validate();
    if (!formHasErrors && !loginFormHasErrors) {
      setLoggingIn(true);
      const { otp } = otpForm.values;
      const { email } = loginForm.values;
      const r = await PerformRequest<NonPaginatedResponse<LoginResponse>>({
        method: "POST",
        route: Endpoints.GetValidateOtpAndLogin(),
        data: {
          code: otp,
          token,
          email,
          stayLoggedIn: isKeepUserLoggedIn,
        },
        callback: () => {
          setLoggingIn(false);
        },
      });
      setLoggingIn(false);

      if (r) {
        if (r.status === 200) {
          localStorage.setItem("t", r.data.data.token);
          navigate("/d/services");
        } else if (r.status === 404) {
          DefaultErrorNotification("Account does not exist!");
        } else if (r.status === 401) {
          DefaultErrorNotification("Invalid OTP");
        } else if (r.status === 400) {
          DefaultErrorNotification("Invalid OTP");
        } else if (r.status === 500) {
          DefaultErrorNotification("An error occurred!");
        }
      } else {
        DefaultErrorNotification("An error occurred!");
      }
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setGoogleLoggingIn(true);
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();

      const r = await PerformRequest<NonPaginatedResponse<LoginResponse>>({
        method: "POST",
        route: Endpoints.GetGoogleLogin(),
        data: {
          idToken,
          stayLoggedIn: isKeepUserLoggedIn,
        },
        callback: () => {
          setGoogleLoggingIn(false);
        },
      });

      if (r && r.status === 200) {
        localStorage.setItem("t", r.data.data.token);
        navigate("/d/services");
      } else {
        DefaultErrorNotification(r?.data?.message || "Google login failed");
      }
    } catch (error: any) {
      setGoogleLoggingIn(false);
      if (error.code !== "auth/popup-closed-by-user") {
        DefaultErrorNotification(error.message || "Google login failed");
      }
    }
  };

  return (
    <div className="login-wrapper">
      {/* ─── OTP Modal ─── */}
      <Modal
        centered
        title="OTP Verification"
        opened={isOtpModalOpen}
        onClose={() => {
          CloseOtpModal();
          otpForm.reset();
        }}
        radius="md"
        size="md"
        padding="xl"
      >
        <div className="flex-col align-center justify-center">
          <ThemeIcon
            size={64}
            radius="xl"
            variant="light"
            color="green"
            mb="lg"
          >
            <IconMail size={32} />
          </ThemeIcon>
          <Text fw={700} fz={20} ta="center" mb="xs">
            Check your Inbox
          </Text>
          <Text c="dimmed" fz="sm" ta="center" mb="xl">
            We've sent a 6-digit code to your email. Enter it below to continue.
          </Text>
          <PinInput
            {...otpForm.getInputProps("otp")}
            my={16}
            size="lg"
            length={ValidOtpLength}
            placeholder="○"
          />
          <Group justify="center" w="100%">
            <Button
              disabled={resendOtpCounter > 0 || isLoggingIn}
              variant="subtle"
              color="gray"
              onClick={ResendOtp}
              fullWidth
              size="sm"
            >
              Resend Code {resendOtpCounter > 0 ? `(${resendOtpCounter}s)` : ""}
            </Button>
            <Button
              fullWidth
              color={Colors.GreenPrimary}
              onClick={ValidateOtpAndLogin}
              loading={isLoggingIn}
              size="sm"
            >
              Verify & Login
            </Button>
          </Group>
        </div>
      </Modal>

      {/* ─── Brand Panel (Left) ─── */}
      <motion.div
        className="login-brand-panel"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="brand-content">
          <img src={LogoFullWhite} alt={AppName} className="brand-logo" />

          <div className="brand-text">
            <motion.h2
              className="brand-headline"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              Save more on the
              <br />
              subscriptions you love.
            </motion.h2>
            <motion.p
              className="brand-subtitle"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              Join thousands of users sharing premium subscriptions at a fraction
              of the cost. Netflix, Spotify, YouTube — all in one place.
            </motion.p>
          </div>

          <motion.div
            className="brand-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.5 }}
          >
            <div className="stat-item">
              <div className="stat-value">10K+</div>
              <div className="stat-label">Active Users</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">12+</div>
              <div className="stat-label">Services</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">60%</div>
              <div className="stat-label">Avg. Savings</div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* ─── Form Panel (Right) ─── */}
      <div className="login-form-panel">
        <motion.div
          className="login-form-inner"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Stack gap={2} mb={32}>
            <Title order={2} fw={800} size={26}>
              Welcome back
            </Title>
            <Text c="dimmed" size="sm" fw={400}>
              Sign in to your account to continue
            </Text>
          </Stack>

          <Stack gap="sm">
            <TextInput
              label="Email"
              placeholder="name@example.com"
              size="sm"
              leftSection={<IconMail size={15} stroke={1.8} />}
              {...loginForm.getInputProps("email")}
              classNames={{ input: "auth-input" }}
            />

            <PasswordInput
              label="Password"
              placeholder="••••••••"
              size="sm"
              leftSection={<IconLock size={15} stroke={1.8} />}
              {...loginForm.getInputProps("password")}
              classNames={{ input: "auth-input" }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  LoginUser();
                }
              }}
            />

            <Group justify="space-between" mt={2}>
              <Checkbox
                label="Remember me"
                checked={isKeepUserLoggedIn}
                onChange={(e) =>
                  setKeepUserLoggedIn(e.currentTarget.checked)
                }
                color={Colors.GreenPrimary}
                size="xs"
              />
              <Anchor
                component={Link}
                to="/r"
                size="xs"
                c={Colors.GreenPrimary}
                fw={600}
              >
                Forgot password?
              </Anchor>
            </Group>

            <Button
              fullWidth
              mt="sm"
              color={Colors.GreenPrimary}
              onClick={LoginUser}
              loading={isLoggingIn}
              className="login-btn"
            >
              Sign In
            </Button>

            <div className="flex-row align-center gap-sm" style={{ margin: "8px 0" }}>
              <div className="flex-1 divider-line"></div>
              <Text size="xs" c="dimmed" fw={600} px={8}>
                or
              </Text>
              <div className="flex-1 divider-line"></div>
            </div>

            <Button
              fullWidth
              variant="default"
              leftSection={<IconBrandGoogle size={16} />}
              onClick={handleGoogleLogin}
              loading={isGoogleLoggingIn}
              className="google-btn"
            >
              Continue with Google
            </Button>
          </Stack>

          <Text ta="center" mt={28} size="sm" c="dimmed">
            Don't have an account?{" "}
            <Anchor
              component="button"
              onClick={() => navigate("/sign-up")}
              c={Colors.GreenPrimary}
              fw={700}
              size="sm"
            >
              Create Account
            </Anchor>
          </Text>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
